import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertCircle, Wallet } from "lucide-react";
import { cn, formatEur } from "@/lib/utils";
import { TABLA_ANIOS, ColsAnios } from "@/components/ui/tabla-anios";
import { getProyeccionConfig, saveProyeccionConfig, getProyeccionResultado, getNofCuentas } from "@/lib/api";
import { codigoVisible, esCuentaDeAsiento, SimboloAsiento } from "@/lib/useModoAsiento";

const CLAVES = ["clientes", "existencias", "proveedores"];
// Punto 12 de Héctor (3-oct-2026): las palancas del bloque excluido se OCULTAN
// de la vista ("actualment no les necessitem"); el cálculo sigue intacto por
// detrás (config.nof.palancas + roll-forward del motor), así que una empresa
// que ya las tuviera configuradas no cambia ni un céntimo.
const MOSTRAR_PALANCAS = false;
const LABELS = { clientes: "Clientes (PMC)", existencias: "Existencias (PME)", proveedores: "Proveedores (PMP)" };

// Exclusiones fijadas en el antiguo popover «Parámetros» de Resultados: un
// importe por bloque en `nof.excluir` SIN cuentas marcadas en
// `nof.excluir_cuentas`. El motor las sigue restando (es el fallback de
// `domain/nof.py`); desde nov-2026 la NOF se edita solo en esta pestaña, así
// que se muestran y editan aquí para que ningún importe del cálculo quede
// oculto. {bloque: {año: importe}}.
const exclusionesLegado = (nofCfg) => {
  const out = {};
  CLAVES.forEach((p) => {
    const serie = nofCfg?.excluir?.[p];
    const conCuentas = (nofCfg?.excluir_cuentas?.[p] || []).length > 0;
    if (!conCuentas && serie && typeof serie === "object" && Object.keys(serie).length) out[p] = serie;
  });
  return out;
};

// Formato único de proyección (lib/utils.formatEur): antes había un tercer
// estilo local ("1.234 €" sin style currency).
const fmt = formatEur;

const normalizarNof = (nof) => ({
  modo: nof?.modo === "dias" ? "dias" : "pct",
  origen: nof?.origen ?? null,
  base_pmp: nof?.base_pmp ?? null,
  clientes: nof?.clientes ?? null,
  existencias: nof?.existencias ?? null,
  proveedores: nof?.proveedores ?? null,
  excluir: nof?.excluir ?? null,
  excluir_manual: nof?.excluir_manual ?? null,
  palancas: nof?.palancas ?? null,
});

export default function NofContent({ empresaId, readOnly }) {
  const [config, setConfig] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dias, setDias] = useState({ clientes: "", existencias: "", proveedores: "" });
  const [cuentasNof, setCuentasNof] = useState(null);
  // Cuentas marcadas para excluir, por partida: { clientes: Set, ... }.
  const [marcadas, setMarcadas] = useState({ clientes: new Set(), existencias: new Set(), proveedores: new Set() });
  // E4: cuentas de ventas (70x) / compras (60x-61x) fuera de la BASE de los plazos.
  const [marcadasBase, setMarcadasBase] = useState({ ventas: new Set(), compras: new Set() });
  // Orden del listado de cuentas a excluir por importe (false = mayor primero).
  const [ordenAsc, setOrdenAsc] = useState(false);
  // Año cuyos saldos se MUESTRAN en las listas de exclusión (petición de Pau
  // 2026-09-14: "que indique qué año es… una ventana por año para ver la
  // evolución"). SOLO visualización: el cálculo de exclusiones sigue anclado
  // al último año real (ultimoReal), se mire el año que se mire.
  const [anioVista, setAnioVista] = useState(null);
  // Hipótesis POR AÑO proyectado (sept-2026): el motor YA soporta
  // config.nof.<partida> = {año: valor} con arrastre del último definido
  // (_param_nof caso "manual por año", candado en test_proyeccion_v3); aquí
  // solo se expone la edición. `modoAnual` activa la rejilla de un input por
  // año; `porAnio` guarda los textos {clave: {año: "valor"}}.
  const [porAnio, setPorAnio] = useState({ clientes: {}, existencias: {}, proveedores: {} });
  const [modoAnual, setModoAnual] = useState({ clientes: false, existencias: false, proveedores: false });
  // E1 (Héctor 29-09): el NOF se divide en 4 pestañas — General (visión
  // conjunta, gráfico y ciclo de caja) y una por bloque (Clientes /
  // Existencias / Proveedores) con SU hipótesis, SUS exclusiones, SUS
  // palancas y SU cascada. Misma información, mismos cálculos: solo se
  // reparte lo que antes era una página larga.
  const [tabNof, setTabNof] = useState("general");
  // E2 (Héctor 29-09): clic en una cifra derivada → se muestra la OPERACIÓN
  // con los importes reales que la producen.
  const [calcSel, setCalcSel] = useState(null);
  // Punto 11 (Héctor 3-oct): el PMC/PME/PMP ajustado proyectado se edita
  // DIRECTAMENTE en la cascada — es la hipótesis manual por año del motor.
  const [editCel, setEditCel] = useState(null); // {clave, anio, txt}
  // Importe manual libre a excluir por bloque (fila "Otros a excluir" de EDSER).
  const [manual, setManual] = useState({ clientes: "", existencias: "", proveedores: "" });
  // E7 (Pau oct-2026): importe manual POR AÑO proyectado ("clave|año" → txt);
  // vacío = arrastra el del último real (un saldo anómalo que se va
  // regularizando puede declinar año a año).
  const [manualAnual, setManualAnual] = useState({});
  // Último bloque tocado, para resaltar su chip en el mini-sumador en vivo.
  const [ultimoToque, setUltimoToque] = useState(null);
  // Palancas del bloque excluido: { "clientes|cobro|2026": "300000", ... }
  const [palancas, setPalancas] = useState({});
  // Texto editable de las exclusiones del antiguo «Parámetros» (ver exclusionesLegado).
  const [legadoTxt, setLegadoTxt] = useState({ clientes: "", existencias: "", proveedores: "" });

  const cargar = useCallback(async () => {
    try {
      const [cfgRes, resRes, cuRes] = await Promise.all([
        getProyeccionConfig(empresaId),
        getProyeccionResultado(empresaId).catch(() => ({ data: null })),
        getNofCuentas(empresaId).catch(() => ({ data: null })),
      ]);
      setConfig(cfgRes.data);
      setResultado(resRes.data);
      setCuentasNof(cuRes.data);
    } catch (e) {
      toast.error("Error al cargar la configuración de NOF");
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => { setLoading(true); cargar(); }, [cargar]);

  const nof = normalizarNof(config?.nof);
  const legado = useMemo(() => exclusionesLegado(config?.nof), [config]);
  const nofNuevo = !!config?.nof && typeof config.nof === "object" && "modo" in config.nof;
  const aniosHist = useMemo(() => [...(resultado?.anios_historicos || [])].sort((a, b) => a - b), [resultado]);
  const ultimoReal = aniosHist[aniosHist.length - 1];
  const aniosProy = useMemo(
    () => [...(resultado?.nof_detalle?.anios_proyectados || resultado?.anios_proyectados || [])].sort((a, b) => a - b),
    [resultado]
  );

  useEffect(() => {
    const n = normalizarNof(config?.nof);
    setDias({
      clientes: n.clientes != null && typeof n.clientes !== "object" ? String(n.clientes) : "",
      existencias: n.existencias != null && typeof n.existencias !== "object" ? String(n.existencias) : "",
      proveedores: n.proveedores != null && typeof n.proveedores !== "object" ? String(n.proveedores) : "",
    });
    // Config con dict {año: valor} → modo "por año" activo con sus casillas.
    const pa = {};
    const ma = {};
    CLAVES.forEach((k) => {
      const v = n[k];
      if (v != null && typeof v === "object") {
        ma[k] = true;
        pa[k] = Object.fromEntries(Object.entries(v).map(([a, x]) => [String(a), String(x)]));
      } else {
        ma[k] = false;
        pa[k] = {};
      }
    });
    setPorAnio(pa);
    setModoAnual(ma);
    const ec = config?.nof?.excluir_cuentas || {};
    setMarcadas({
      clientes: new Set(ec.clientes || []),
      existencias: new Set(ec.existencias || []),
      proveedores: new Set(ec.proveedores || []),
    });
    // E4 (Pau oct-2026): cuentas de ventas/compras marcadas FUERA DE LA BASE
    // de los plazos (PMC/PME/PMP).
    setMarcadasBase({
      ventas: new Set(config?.nof?.base_excluir_ventas || []),
      compras: new Set(config?.nof?.base_excluir_compras || []),
    });
    const em = config?.nof?.excluir_manual || {};
    const val = (p) => {
      const serie = em[p] || {};
      const v = ultimoReal != null ? serie[String(ultimoReal)] : Object.values(serie)[0];
      return v != null ? String(v) : "";
    };
    setManual({ clientes: val("clientes"), existencias: val("existencias"), proveedores: val("proveedores") });
    const ma2 = {};
    ["clientes", "existencias", "proveedores"].forEach((p) => {
      Object.entries(em[p] || {}).forEach(([a, v]) => {
        if (String(a) !== String(ultimoReal) && v != null) ma2[`${p}|${a}`] = String(v);
      });
    });
    setManualAnual(ma2);
    const pal = {};
    Object.entries(config?.nof?.palancas || {}).forEach(([p, tipos]) => {
      Object.entries(tipos || {}).forEach(([t, serie]) => {
        Object.entries(serie || {}).forEach(([anio, v]) => { pal[`${p}|${t}|${anio}`] = String(v); });
      });
    });
    setPalancas(pal);
    const leg = exclusionesLegado(config?.nof);
    const valLeg = (p) => {
      const serie = leg[p];
      if (!serie) return "";
      // Solo el importe del ÚLTIMO REAL cuenta en el motor (es el ancla de la
      // proyección); el de otro año se enseña aparte, como aviso.
      const v = serie[String(ultimoReal)];
      return v != null ? String(v) : "";
    };
    setLegadoTxt({ clientes: valLeg("clientes"), existencias: valLeg("existencias"), proveedores: valLeg("proveedores") });
  }, [config, ultimoReal]);

  const parsear = (t) => {
    const s = String(t ?? "").trim().replace(",", ".");
    if (s === "") return null;
    const v = parseFloat(s);
    return isNaN(v) ? undefined : v;
  };

  const guardar = async (nofPayload, msg) => {
    try {
      const res = await saveProyeccionConfig(empresaId, { nof: { modo: nof.modo, ...nofPayload } });
      setConfig(res.data);
      toast.success(msg);
      // recargar el resultado (los saldos han cambiado)
      const r = await getProyeccionResultado(empresaId).catch(() => ({ data: null }));
      setResultado(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const handleModo = (modo) => {
    if (modo === nof.modo) return;
    guardar({ modo, clientes: null, existencias: null, proveedores: null }, modo === "dias" ? "NOF en días" : "NOF en % s/ventas");
  };
  const handleDiasBlur = (clave) => () => {
    const v = parsear(dias[clave]);
    if (v === undefined) return;
    guardar({ [clave]: v }, "Hipótesis de NOF actualizada");
  };
  // Vuelve una partida a "Automático": vacía el campo → el día se recalcula solo
  // con el histórico y las cuentas/importes excluidos.
  const volverAuto = (clave) => {
    setDias((v) => ({ ...v, [clave]: "" }));
    setModoAnual((v) => ({ ...v, [clave]: false }));
    setPorAnio((v) => ({ ...v, [clave]: {} }));
    guardar({ [clave]: null }, `${LABELS[clave].split(" ")[0]} en automático`);
  };

  // ── Hipótesis POR AÑO (sept-2026) ──────────────────────────────────────
  const guardarPorAnio = (clave) => {
    const dict = {};
    Object.entries(porAnio[clave] || {}).forEach(([anio, txt]) => {
      const v = parsear(txt);
      if (v != null && v !== undefined && v >= 0) dict[anio] = v;
    });
    guardar(
      { [clave]: Object.keys(dict).length ? dict : null },
      Object.keys(dict).length
        ? `${LABELS[clave].split(" ")[0]}: hipótesis por año guardada`
        : `${LABELS[clave].split(" ")[0]} en automático`
    );
  };
  const activarPorAnio = (clave) => {
    // Precarga el valor único actual (si lo hay) en el primer año proyectado.
    const base = String(dias[clave] ?? "").trim();
    setPorAnio((v) => {
      const actual = v[clave] || {};
      if (Object.keys(actual).length || !base || !aniosProy.length) return v;
      return { ...v, [clave]: { [String(aniosProy[0])]: base } };
    });
    setModoAnual((v) => ({ ...v, [clave]: true }));
  };
  const desactivarPorAnio = (clave) => {
    // Vuelve al valor único: usa el del primer año definido (o automático).
    const vals = porAnio[clave] || {};
    const definidos = Object.keys(vals)
      .filter((a) => String(vals[a]).trim() !== "")
      .sort();
    const unico = definidos.length ? parsear(vals[definidos[0]]) : null;
    setModoAnual((v) => ({ ...v, [clave]: false }));
    setDias((v) => ({ ...v, [clave]: unico != null ? String(unico) : "" }));
    guardar(
      { [clave]: unico != null ? unico : null },
      unico != null
        ? `${LABELS[clave].split(" ")[0]}: valor único restaurado`
        : `${LABELS[clave].split(" ")[0]} en automático`
    );
  };
  // Importe manual libre a excluir por bloque (fila "Otros a excluir manual" de
  // EDSER). Se ancla al último año real (el motor lo mantiene plano en la
  // proyección) y se SUMA a la exclusión por cuenta en el motor.
  const handleManualBlur = () => {
    const em = {};
    CLAVES.forEach((p) => {
      const serie = {};
      const v = parsear(manual[p]);
      if (v && v > 0 && ultimoReal != null) serie[String(ultimoReal)] = v;
      // E7: valores explícitos por año proyectado (mandan sobre el arrastre).
      Object.entries(manualAnual).forEach(([k, txt]) => {
        const [pp, anio] = k.split("|");
        if (pp !== p) return;
        const va = parsear(txt);
        if (va != null && va >= 0 && txt !== "") serie[anio] = va;
      });
      if (Object.keys(serie).length) em[p] = serie;
    });
    guardar({ excluir_manual: Object.keys(em).length ? em : null }, "Exclusión manual actualizada");
  };
  // Palancas: reconstruye {partida: {cobro|baja: {año: importe}}} desde el estado plano.
  const handlePalancaBlur = () => {
    const out = {};
    Object.entries(palancas).forEach(([k, txt]) => {
      const v = parsear(txt);
      if (!v || v <= 0) return;
      const [p, t, anio] = k.split("|");
      out[p] = out[p] || {};
      out[p][t] = out[p][t] || {};
      out[p][t][anio] = v;
    });
    guardar({ palancas: Object.keys(out).length ? out : null }, "Palancas del bloque actualizadas");
  };
  // Marca/desmarca una cuenta y recalcula el importe a excluir por año (suma de
  // las cuentas marcadas), guardando importes (para el motor) y códigos (UI).
  const toggleCuenta = (partida, codigo) => {
    const nueva = { ...marcadas, [partida]: new Set(marcadas[partida]) };
    if (nueva[partida].has(codigo)) nueva[partida].delete(codigo);
    else nueva[partida].add(codigo);
    setMarcadas(nueva);
    setUltimoToque(partida);
    const excluir = {};
    const codigos = {};
    CLAVES.forEach((p) => {
      const set = nueva[p];
      if (!set || set.size === 0) return;
      codigos[p] = [...set];
      const serie = {};
      (cuentasNof?.partidas?.[p] || []).forEach((c) => {
        if (set.has(c.codigo)) {
          Object.entries(c.por_anio || {}).forEach(([anio, saldo]) => {
            serie[anio] = (serie[anio] || 0) + (saldo || 0);
          });
        }
      });
      if (Object.keys(serie).length) excluir[p] = serie;
    });
    // Un bloque SIN cuentas marcadas conserva su importe del antiguo
    // «Parámetros»: antes, marcar una cuenta en Clientes lo borraba también
    // en Existencias y Proveedores.
    CLAVES.forEach((p) => {
      if (!excluir[p] && !(nueva[p] && nueva[p].size) && legado[p]) excluir[p] = legado[p];
    });
    guardar(
      {
        excluir: Object.keys(excluir).length ? excluir : null,
        excluir_cuentas: Object.keys(codigos).length ? codigos : null,
      },
      "Cuentas excluidas del NOF actualizadas"
    );
  };

  // Edita o quita el importe del antiguo «Parámetros» de un bloque, sin tocar
  // el resto de `nof.excluir` (bloques con cuentas marcadas incluidos).
  // El importe se guarda en el año del ÚLTIMO REAL (donde lo lee el motor, igual
  // que hacía el antiguo popover) sin borrar los de otros años; «Quitar» borra
  // el bloque entero.
  const guardarLegado = (clave, texto, quitarTodo = false) => {
    const v = parsear(texto);
    if (v === undefined || ultimoReal == null) return;
    const excluir = { ...(config?.nof?.excluir || {}) };
    const serie = { ...(legado[clave] || {}) };
    delete serie[String(ultimoReal)];
    delete serie[ultimoReal];
    if (!quitarTodo && v != null && v > 0) serie[String(ultimoReal)] = v;
    if (quitarTodo || !Object.keys(serie).length) delete excluir[clave];
    else excluir[clave] = serie;
    guardar({ excluir: Object.keys(excluir).length ? excluir : null },
      !quitarTodo && v != null && v > 0 ? "Importe excluido actualizado" : "Importe excluido quitado");
  };

  // E4 (Pau oct-2026): marca/desmarca una cuenta de ventas o compras FUERA DE
  // LA BASE de los plazos. Se guardan los códigos; el backend deriva el
  // importe de cada año (histórico y proyectado) de esas mismas cuentas.
  const toggleCuentaBase = (clave, codigo) => {
    const nueva = { ...marcadasBase, [clave]: new Set(marcadasBase[clave]) };
    if (nueva[clave].has(codigo)) nueva[clave].delete(codigo);
    else nueva[clave].add(codigo);
    setMarcadasBase(nueva);
    guardar(
      {
        base_excluir_ventas: nueva.ventas.size ? [...nueva.ventas] : null,
        base_excluir_compras: nueva.compras.size ? [...nueva.compras] : null,
      },
      "Base de los plazos actualizada"
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }
  if (!nofNuevo) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>El backend no expone la configuración de NOF ampliada.</p>
      </div>
    );
  }

  const unidad = nof.modo === "dias" ? "días" : "% s/ventas";
  // Suma EN VIVO de lo que se está excluyendo en un bloque (cuentas marcadas al
  // último real + importe manual). Se recalcula al instante al marcar/teclear,
  // sin esperar al servidor.
  const sumaExcluida = (clave) => {
    const a = String(ultimoReal ?? "");
    const set = marcadas[clave];
    let cuentas = 0, n = 0;
    (cuentasNof?.partidas?.[clave] || []).forEach((c) => {
      if (set?.has(c.codigo)) { cuentas += c.por_anio?.[a] ?? 0; n += 1; }
    });
    const man = parsear(manual[clave]) || 0;
    const leg = n === 0 && legado[clave] ? (parsear(legadoTxt[clave]) || 0) : 0;
    return { total: cuentas + leg + (man > 0 ? man : 0), cuentas: cuentas + leg, manual: man > 0 ? man : 0, n };
  };
  // Referencia histórica (media/último) por partida, para que el usuario sepa el
  // valor de partida antes de cambiarlo.
  const ratios = resultado?.nof_ratios || {};
  const uds = nof.modo === "dias" ? "d" : "%";
  const refTxt = (clave) => {
    const r = ratios[clave];
    if (!r) return null;
    const f = (x) => (x == null ? "—" : `${Math.round(x)}${uds}`);
    return `Media: ${f(r.media)} · Último: ${f(r.ultimo)}`;
  };

  // ── Detalle NOF estilo EDSER: cascada bruto → exclusión → ajustado, días
  // general/ajustado por bloque y año (histórico + proyectado) + NOF y ciclo.
  const det = resultado?.nof_detalle;
  const aniosDet = det ? [...(det.anios_historicos || []), ...(det.anios_proyectados || [])] : [];
  const proySet = new Set((det?.anios_proyectados || []).map(Number));
  const esProy = (y) => proySet.has(Number(y));
  const cel = (bloque, y, campo) => det?.[bloque]?.por_anio?.[String(y)]?.[campo];
  const celNof = (y, campo) => det?.nof?.por_anio?.[String(y)]?.[campo];
  const eur = formatEur;
  const diaF = (v) => (v == null || isNaN(v) ? "—" : `${Math.round(v)}${uds}`);
  const pct1 = (v) => (v == null || isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`);
  const diasMode = det?.modo === "dias";
  // E2: operación con importes reales de una celda derivada (clic para verla).
  const opRatio = (num, base, y) => {
    const n = num == null || isNaN(num) ? null : eur(num);
    const b = base == null || isNaN(base) ? null : eur(base);
    if (n == null || b == null) return null;
    return diasMode ? `${n} ÷ ${b} × 365` : `${n} ÷ ${b} × 100`;
  };
  const calcAjustado = (clave) => (y) => {
    const b = cel(clave, y, "bruto");
    const ec = cel(clave, y, "excluir_cuentas");
    const em = cel(clave, y, "excluir_manual");
    const aj = cel(clave, y, "ajustado");
    if (b == null || aj == null) return null;
    return `${eur(b)} (bruto) − ${eur(ec || 0)} (cuentas excl.) − ${eur(em || 0)} (manual) = ${eur(aj)}`;
  };
  const calcDia = (clave, campoNum, campoBase, res) => (y) =>
    (() => {
      const op = opRatio(cel(clave, y, campoNum), cel(clave, y, campoBase), y);
      const r = cel(clave, y, res);
      return op && r != null ? `${op} = ${diaF(r)}` : null;
    })();
  const calcNof = (campoRes) => (y) => {
    const e = cel("existencias", y, campoRes);
    const c = cel("clientes", y, campoRes);
    const p = cel("proveedores", y, campoRes);
    const r = celNof(y, campoRes === "bruto" ? "general" : "ajustada");
    if ([e, c, p, r].some((v) => v == null || isNaN(v))) return null;
    return `${eur(e)} (existencias) + ${eur(c)} (deudores) − ${eur(p)} (acreedores) = ${eur(r)}`;
  };
  const calcNofVentas = (campo) => (y) => {
    const nof = celNof(y, campo === "general_ventas" ? "general" : "ajustada");
    const v = cel("clientes", y, "base");
    const r = celNof(y, campo);
    if ([nof, v, r].some((x) => x == null || isNaN(x))) return null;
    return `${eur(nof)} ÷ ${eur(v)} (ventas) × 100 = ${pct1(r)}`;
  };
  const calcCiclo = (aj) => (y) => {
    const pme = cel("existencias", y, aj ? "dia_ajustado" : "dia_general");
    const pmc = cel("clientes", y, aj ? "dia_ajustado" : "dia_general");
    const pmp = cel("proveedores", y, aj ? "dia_serv_ajustado" : "dia_serv_general");
    const r = celNof(y, aj ? "ciclo_ajustado" : "ciclo_general");
    if ([pme, pmc, pmp, r].some((x) => x == null || isNaN(x))) return null;
    return `${diaF(pme)} (PME) + ${diaF(pmc)} (PMC) − ${diaF(pmp)} (PMP) = ${diaF(r)}`;
  };
  const calcVar = (y) => {
    const i = aniosDet.indexOf(y);
    if (i <= 0) return null;
    const prev = celNof(aniosDet[i - 1], "ajustada");
    const act = celNof(y, "ajustada");
    const r = celNof(y, "var_ajustada");
    if ([prev, act, r].some((x) => x == null || isNaN(x))) return null;
    return `${eur(act)} (NOF ${y}) − ${eur(prev)} (NOF ${aniosDet[i - 1]}) = ${eur(r)} → ${r > 0 ? "consume caja" : "libera caja"}`;
  };

  // ── Edición del ratio ajustado EN la cascada (punto 11, Héctor 3-oct) ───
  // La cadena pactada: PMC/PME/PMP ajustado = INPUT manual por año → el motor
  // calcula el saldo ajustado (base × días/365), le suma el bloque excluido
  // (bruto del balance) y lleva la variación a la NOF/CFO. Al editar por
  // primera vez se CONGELA el valor efectivo actual de todos los años
  // proyectados (lo que se ve pasa a ser el input) y se cambia solo el año
  // tocado — sin saltos ocultos en los demás años.
  const campoDiaHipotesis = (clave) =>
    clave === "proveedores"
      ? (diasMode && nof.base_pmp === "compras_servicios" ? "dia_serv_ajustado" : "dia_compras_ajustado")
      : "dia_ajustado";
  const guardarDiaCascada = (clave, anio) => {
    const v = parsear(editCel?.txt);
    setEditCel(null);
    if (v == null || v === undefined || v < 0) return;
    const actual = nof[clave];
    const dict = {};
    if (actual && typeof actual === "object") {
      Object.entries(actual).forEach(([a, val]) => { dict[a] = val; });
    } else {
      aniosProy.forEach((y) => {
        const cur = cel(clave, y, campoDiaHipotesis(clave));
        if (cur != null && !isNaN(cur)) dict[String(y)] = Math.round(cur * 100) / 100;
      });
    }
    dict[String(anio)] = v;
    guardar({ [clave]: dict }, `${LABELS[clave].split(" ")[0]}: hipótesis de ${anio} guardada`);
  };

  // Filas de la cascada EDSER por BLOQUE (E1: cada pestaña enseña las suyas;
  // General enseña NOF y ciclo). get(y) lee de nof_detalle; calc(y) = la
  // operación con importes (E2, clic en la celda).
  const filasClientes = [
    { s: "CLIENTES — periodo de cobro (PMC)" },
    { l: "Ventas (INCN)", g: (y) => cel("clientes", y, "base"), f: eur, hint: "De la P&G (cifra de negocio)" },
    { l: "Deudores — bruto (balance)", g: (y) => cel("clientes", y, "bruto"), f: eur, hint: "Saldo del balance: lo que te deben" },
    { l: "(−) Vinculadas / dudoso (cuentas)", g: (y) => cel("clientes", y, "excluir_cuentas"), f: eur, tone: "excl", hint: "Intercompany/dudoso de las cuentas marcadas" },
    { l: "(±) Movimientos manuales (Otras hipótesis)", g: (y) => cel("clientes", y, "evento_manual"), f: eur, hint: "Eventos de balance declarados en Otras hipótesis (informativo: ya están en el bruto)" },
    { l: "(−) Eventos fuera de la NOF", g: (y) => cel("clientes", y, "excluir_evento"), f: eur, tone: "excl", hint: "Parte de los eventos marcada como «no afecta a la NOF»: va al bloque excluido" },
    { l: "(−) Otros a excluir (manual)", g: (y) => cel("clientes", y, "excluir_manual"), f: eur, tone: "excl", hint: "Importe manual libre" },
    { l: "= Deudores ajustados", g: (y) => cel("clientes", y, "ajustado"), f: eur, tone: "strong", hint: "Bruto − exclusiones (operativo)", calc: calcAjustado("clientes") },
    { l: "PMC general", g: (y) => cel("clientes", y, "dia_general"), f: diaF, hint: "Deudores bruto ÷ Ventas × 365", calc: calcDia("clientes", "bruto", "base", "dia_general") },
    { l: "PMC ajustado", g: (y) => cel("clientes", y, "dia_ajustado"), f: diaF, tone: "strong", hint: "Deudores ajustados ÷ Ventas × 365 — en proyección es TU hipótesis: clic para editarla", calc: calcDia("clientes", "ajustado", "base", "dia_ajustado"), edit: "clientes" },
  ];
  const filasProveedores = [
    { s: "PROVEEDORES — periodo de pago (PMP)" },
    { l: "Compras / aprovisionamientos", g: (y) => cel("proveedores", y, "base_compras"), f: eur, hint: "De la P&G (compras de material)" },
    ...(diasMode ? [{ l: "Servicios / otros gastos", g: (y) => cel("proveedores", y, "servicios"), f: eur, hint: "De la P&G (servicios y otros gastos)" }] : []),
    { l: "Acreedores — bruto (balance)", g: (y) => cel("proveedores", y, "bruto"), f: eur, hint: "Saldo del balance: lo que debes" },
    { l: "(−) Vinculadas / no comercial (cuentas)", g: (y) => cel("proveedores", y, "excluir_cuentas"), f: eur, tone: "excl", hint: "Proveedores vinculados de las cuentas marcadas" },
    { l: "(±) Movimientos manuales (Otras hipótesis)", g: (y) => cel("proveedores", y, "evento_manual"), f: eur, hint: "Eventos de balance declarados en Otras hipótesis (informativo: ya están en el bruto)" },
    { l: "(−) Eventos fuera de la NOF", g: (y) => cel("proveedores", y, "excluir_evento"), f: eur, tone: "excl", hint: "Parte de los eventos marcada como «no afecta a la NOF»: va al bloque excluido" },
    { l: "(−) Otros a excluir (manual)", g: (y) => cel("proveedores", y, "excluir_manual"), f: eur, tone: "excl", hint: "Importe manual libre" },
    { l: "= Acreedores ajustados", g: (y) => cel("proveedores", y, "ajustado"), f: eur, tone: "strong", hint: "Bruto − exclusiones", calc: calcAjustado("proveedores") },
    { l: "PMP s/ compras — general", g: (y) => cel("proveedores", y, "dia_compras_general"), f: diaF, hint: "Acreedores ÷ Compras × 365 (base estrecha)", calc: calcDia("proveedores", "bruto", "base_compras", "dia_compras_general") },
    { l: "PMP s/ compras — ajustado", g: (y) => cel("proveedores", y, "dia_compras_ajustado"), f: diaF, hint: "Acreedores ajustados ÷ Compras × 365", calc: calcDia("proveedores", "ajustado", "base_compras", "dia_compras_ajustado"),
      ...(campoDiaHipotesis("proveedores") === "dia_compras_ajustado" ? { edit: "proveedores" } : {}) },
    ...(diasMode ? [
      { l: "PMP s/ compras+serv. — general", g: (y) => cel("proveedores", y, "dia_serv_general"), f: diaF, hint: "÷ (Compras+Servicios) × 365 ← base recomendada", calc: calcDia("proveedores", "bruto", "base_compras_serv", "dia_serv_general") },
      { l: "PMP s/ compras+serv. — ajustado", g: (y) => cel("proveedores", y, "dia_serv_ajustado"), f: diaF, tone: "strong", hint: "Ajustado ÷ (Compras+Servicios) × 365", calc: calcDia("proveedores", "ajustado", "base_compras_serv", "dia_serv_ajustado"),
        ...(campoDiaHipotesis("proveedores") === "dia_serv_ajustado" ? { edit: "proveedores" } : {}) },
    ] : []),
  ];
  const filasExistencias = [
    { s: "EXISTENCIAS — periodo de stock (PME)" },
    { l: "Existencias — bruto (balance)", g: (y) => cel("existencias", y, "bruto"), f: eur, hint: "Saldo del balance: stock" },
    { l: "(−) Deteriorado (cuentas)", g: (y) => cel("existencias", y, "excluir_cuentas"), f: eur, tone: "excl", hint: "Stock excluido de las cuentas marcadas" },
    { l: "(±) Movimientos manuales (Otras hipótesis)", g: (y) => cel("existencias", y, "evento_manual"), f: eur, hint: "Eventos de balance declarados en Otras hipótesis (informativo: ya están en el bruto)" },
    { l: "(−) Eventos fuera de la NOF", g: (y) => cel("existencias", y, "excluir_evento"), f: eur, tone: "excl", hint: "Parte de los eventos marcada como «no afecta a la NOF»: va al bloque excluido" },
    { l: "(−) Otros a excluir (manual)", g: (y) => cel("existencias", y, "excluir_manual"), f: eur, tone: "excl", hint: "Importe manual libre (p.ej. stock deteriorado)" },
    { l: "= Existencias ajustadas", g: (y) => cel("existencias", y, "ajustado"), f: eur, tone: "strong", hint: "Bruto − exclusiones (stock sano)", calc: calcAjustado("existencias") },
    { l: "PME general", g: (y) => cel("existencias", y, "dia_general"), f: diaF, hint: "Existencias bruto ÷ Consumo × 365", calc: calcDia("existencias", "bruto", "base", "dia_general") },
    { l: "PME ajustado", g: (y) => cel("existencias", y, "dia_ajustado"), f: diaF, tone: "strong", hint: "Existencias ajustadas ÷ Consumo × 365 — en proyección es TU hipótesis: clic para editarla", calc: calcDia("existencias", "ajustado", "base", "dia_ajustado"), edit: "existencias" },
  ];
  const filasNof = [
    { s: "NOF Y CICLO DE CAJA" },
    { l: "NOF general", g: (y) => celNof(y, "general"), f: eur, hint: "Exist. + Deudores − Acreedores (brutos)", calc: calcNof("bruto") },
    { l: "= NOF ajustada (real)", g: (y) => celNof(y, "ajustada"), f: eur, tone: "nof", hint: "Exist. + Deudores − Acreedores (ajustados) ← la cifra real", calc: calcNof("ajustado") },
    { l: "NOF general / Ventas", g: (y) => celNof(y, "general_ventas"), f: pct1, hint: "NOF general ÷ Ventas", calc: calcNofVentas("general_ventas") },
    { l: "NOF ajustada / Ventas", g: (y) => celNof(y, "ajustada_ventas"), f: pct1, hint: "NOF ajustada ÷ Ventas", calc: calcNofVentas("ajustada_ventas") },
    ...(diasMode ? [
      { l: "Ciclo maduración general", g: (y) => celNof(y, "ciclo_general"), f: diaF, hint: "PME + PMC − PMP (con todo)", calc: calcCiclo(false) },
      { l: "Ciclo maduración ajustado", g: (y) => celNof(y, "ciclo_ajustado"), f: diaF, hint: "PME + PMC − PMP (ajustados)", calc: calcCiclo(true) },
    ] : []),
    { l: "Var. NOF ajustada", g: (y) => celNof(y, "var_ajustada"), f: eur, hint: "Δ vs año anterior: + consume caja / − libera caja", calc: calcVar },
  ];
  // E1: qué filas ve cada pestaña. General = visión conjunta (NOF y ciclo);
  // cada bloque, su cascada.
  const filasPorTab = {
    general: [...filasNof],
    clientes: filasClientes,
    existencias: filasExistencias,
    proveedores: filasProveedores,
  };
  const filasDet = det ? (filasPorTab[tabNof] || filasNof) : [];

  const TABS_NOF = [
    { id: "general", label: "General" },
    { id: "clientes", label: "Clientes" },
    { id: "existencias", label: "Existencias" },
    { id: "proveedores", label: "Proveedores" },
  ];
  const clavesVista = tabNof === "general" ? CLAVES : [tabNof];

  return (
    <div className="space-y-6" data-testid="nof-content">
      <p className="text-sm text-muted-foreground">
        Necesidades Operativas de Fondos: proyecta Existencias, Clientes y Proveedores como saldo del balance a partir de sus días/porcentaje de rotación. NOF = Existencias + Clientes − Proveedores.
      </p>

      {/* E1: sub-pestañas del NOF */}
      <div className="flex flex-wrap gap-1 rounded-lg bg-muted/40 p-1 w-fit">
        {TABS_NOF.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => { setTabNof(t.id); setCalcSel(null); }}
            data-testid={`nof-tab-${t.id}`}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm transition-colors",
              tabNof === t.id
                ? "bg-background shadow font-semibold"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /> {tabNof === "general" ? "Hipótesis de rotación" : `Hipótesis — ${LABELS[tabNof]?.split(" ")[0] || ""}`}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {tabNof === "general" && (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Unidad</Label>
              <div className="flex rounded-md border overflow-hidden">
                {[{ id: "pct", label: "%" }, { id: "dias", label: "días" }].map((m) => (
                  <button key={m.id} type="button" disabled={readOnly} onClick={() => handleModo(m.id)}
                    className={cn("px-3 py-1 text-xs", nof.modo === m.id ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:bg-muted/40")}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {nof.modo === "dias" && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">El automático usa</Label>
                  <select className="h-8 rounded-md border bg-background text-sm px-2" value={nof.origen || "media"} disabled={readOnly}
                    onChange={(e) => guardar({ origen: e.target.value || null }, "Origen de los días NOF actualizado")}>
                    <option value="media">Media histórica</option>
                    <option value="ultimo">Último año</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Base del PMP</Label>
                  <select className="h-8 rounded-md border bg-background text-sm px-2" value={nof.base_pmp || "compras"} disabled={readOnly}
                    onChange={(e) => guardar({ base_pmp: e.target.value || null }, "Base del PMP actualizada")}>
                    <option value="compras">Solo compras</option>
                    <option value="compras_servicios">Compras + servicios</option>
                  </select>
                </div>
              </>
            )}
          </div>
          )}

          <div className={cn("grid grid-cols-1 gap-3", tabNof === "general" && "sm:grid-cols-3")}>
            {clavesVista.map((clave) => {
              const esAnual = !!modoAnual[clave];
              const hayAnual = Object.values(porAnio[clave] || {}).some((t) => String(t).trim() !== "");
              const esManual = esAnual ? hayAnual : String(dias[clave] ?? "") !== "";
              const aplica = ratios[clave]?.efectivo;
              // Evolución REAL por año (ratio ajustado que ya calcula el
              // motor en nof_detalle): histórico en gris, proyectado en amarillo.
              const campoEvol =
                clave !== "proveedores"
                  ? "dia_ajustado"
                  : nof.modo === "dias"
                  ? (nof.base_pmp === "compras_servicios" ? "dia_serv_ajustado" : "dia_compras_ajustado")
                  : "dia_ajustado";
              return (
                <div key={clave} className={cn("space-y-1.5 rounded-md border p-2.5",
                  esManual ? "border-amber-300 dark:border-amber-800" : "border-emerald-300 dark:border-emerald-800")}>
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs font-medium">{LABELS[clave]}</Label>
                    <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide",
                      esManual ? "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                               : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400")}>
                      {esManual ? (esAnual ? "Por año" : "Manual") : "Automático"}
                    </span>
                  </div>
                  {!esAnual ? (
                    <Input type="number" step="0.1" min="0" className="h-8" placeholder="vacío = automático"
                      value={dias[clave] ?? ""} disabled={readOnly}
                      onChange={(e) => setDias((v) => ({ ...v, [clave]: e.target.value }))}
                      onBlur={handleDiasBlur(clave)} data-testid={`nof-dias-${clave}`} />
                  ) : (
                    <div className="space-y-1">
                      <div className="grid grid-cols-2 gap-1">
                        {aniosProy.map((y) => (
                          <div key={y} className="flex items-center gap-1">
                            <span className="text-[11px] text-muted-foreground tabular-nums w-8 shrink-0">{y}</span>
                            <Input type="number" step="0.1" min="0" className="h-7 text-xs px-1.5"
                              placeholder="↧" title={`Vacío: arrastra el valor del año anterior definido`}
                              value={porAnio[clave]?.[String(y)] ?? ""} disabled={readOnly}
                              onChange={(e) => setPorAnio((v) => ({
                                ...v,
                                [clave]: { ...v[clave], [String(y)]: e.target.value },
                              }))}
                              onBlur={() => guardarPorAnio(clave)}
                              data-testid={`nof-anio-${clave}-${y}`} />
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-muted-foreground">Vacío = arrastra el año anterior definido.</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold tabular-nums">
                      → Aplica: {esAnual && hayAnual
                        ? "por año"
                        : aplica == null ? "—" : `${Math.round(aplica)} ${nof.modo === "dias" ? "días" : "%"}`}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      {!readOnly && (
                        esAnual ? (
                          <button type="button" onClick={() => desactivarPorAnio(clave)}
                            className="text-[11px] text-primary hover:underline"
                            data-testid={`nof-unico-${clave}`}>Valor único</button>
                        ) : (
                          <button type="button" onClick={() => activarPorAnio(clave)}
                            className="text-[11px] text-primary hover:underline"
                            title="Un valor distinto por año proyectado (p.ej. el PMC baja de 149 a 120 días)"
                            data-testid={`nof-por-anio-${clave}`}>Por año</button>
                        )
                      )}
                      {esManual && !readOnly && (
                        <button type="button" onClick={() => volverAuto(clave)}
                          className="text-[11px] text-primary hover:underline">↺ Automático</button>
                      )}
                    </span>
                  </div>
                  {refTxt(clave) && (
                    <p className="text-[11px] text-muted-foreground/80 tabular-nums">Histórico → {refTxt(clave)}</p>
                  )}
                  {det && aniosDet.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1 border-t border-dashed"
                      title="Evolución real del ratio AJUSTADO año a año (gris = histórico, amarillo = proyectado). Es el mismo cálculo de la tabla de abajo.">
                      {aniosDet.map((y) => {
                        const v = cel(clave, y, campoEvol);
                        return (
                          <span key={y} className={cn(
                            "rounded px-1 py-0.5 text-[10px] font-mono tabular-nums leading-none",
                            esProy(y) ? "bg-proy text-proy font-semibold" : "bg-muted text-muted-foreground")}>
                            {String(y).slice(2)}·{v == null || isNaN(v) ? "—" : Math.round(v)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {tabNof === "general" && (
          <div className="rounded-md bg-muted/40 border px-3 py-2 text-[11px] text-muted-foreground leading-relaxed">
            <b>Automático</b> (campo vacío): el {unidad.split(" ")[0]} se calcula solo con tu histórico y baja/sube según las <b>cuentas e importes que excluyas</b> en la pestaña de cada bloque. Quitas una cuenta → se recalcula al momento.<br />
            <b>Manual</b>: escribes un número y se fija (la exclusión ya no cambia el {unidad.split(" ")[0]}, pero el importe excluido se sigue restando del saldo). Pulsa <b>↺ Automático</b> para volver.<br />
            <b>Por año</b>: un valor distinto para cada año proyectado (p.ej. el PMC baja de 149 → 130 → 120); los años vacíos arrastran el último definido. La tira de números bajo cada tarjeta es la <b>evolución real</b> del ratio ajustado año a año (gris histórico · amarillo proyectado), el mismo cálculo de la tabla de abajo.
          </div>
          )}
        </CardContent>
      </Card>

      {tabNof === "general" && det && aniosDet.length > 0 && (
        <NofEvolucionChart det={det} aniosDet={aniosDet} esProy={esProy} />
      )}

      {det && aniosDet.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>
              {tabNof === "general"
                ? "Detalle NOF — ciclo de caja (bruto vs ajustado)"
                : `Detalle ${LABELS[tabNof]?.split(" ")[0] || ""} — cascada bruto → ajustado`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table className={cn(TABLA_ANIOS, "text-xs")}>
              <ColsAnios n={aniosDet.length} />
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="sticky left-0 z-20 bg-muted/40">Concepto</TableHead>
                  {aniosDet.map((y) => (
                    <TableHead key={y} className={cn("text-right whitespace-nowrap", esProy(y) && "text-proy")}>
                      {y}
                      <span className="block text-[10px] font-normal opacity-70">{esProy(y) ? "Proy." : "Hist."}</span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filasDet.map((row, ri) =>
                  row.s ? (
                    <TableRow key={`s-${ri}`} className="bg-primary/10">
                      <TableCell colSpan={aniosDet.length + 1} className="sticky left-0 font-semibold text-[11px] uppercase tracking-wide text-primary py-1">
                        {row.s}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={`r-${ri}`}
                      className={cn("hover:bg-muted/20",
                        row.tone === "excl" && "bg-amber-50/60 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400",
                        row.tone === "strong" && "font-semibold",
                        row.tone === "nof" && "font-bold bg-primary/5 border-y")}>
                      <TableCell title={row.hint}
                        className={cn("sticky left-0 z-10 bg-background",
                          row.tone === "excl" && "bg-amber-50/60 dark:bg-amber-950/20",
                          row.tone === "nof" && "bg-primary/5",
                          (row.tone === "strong" || row.tone === "nof") && "font-semibold")}>
                        {row.l}
                      </TableCell>
                      {aniosDet.map((y) => {
                        // Punto 11: la fila de la HIPÓTESIS (ajustado) se edita
                        // en los años proyectados; en históricos sigue el clic
                        // de "ver el cálculo".
                        const editable = row.edit && esProy(y) && !readOnly;
                        const enEdicion = editable && editCel &&
                          editCel.clave === row.edit && editCel.anio === y;
                        return (
                        <TableCell key={y}
                          onClick={enEdicion ? undefined : editable ? () => {
                            const cur = row.g(y);
                            setEditCel({
                              clave: row.edit, anio: y,
                              txt: cur != null && !isNaN(cur)
                                ? String(Math.round(cur * 100) / 100).replace(".", ",")
                                : "",
                            });
                          } : row.calc ? () => {
                            const texto = row.calc(y);
                            if (texto) setCalcSel({ label: row.l, anio: y, texto });
                          } : undefined}
                          title={editable
                            ? "Clic: editar la hipótesis de este año"
                            : row.calc ? "Clic: ver el cálculo con los importes" : undefined}
                          className={cn("text-right font-mono tabular-nums whitespace-nowrap",
                            esProy(y) && "bg-proy",
                            (row.calc || editable) && "cursor-pointer",
                            row.calc && !editable && "underline decoration-dotted decoration-muted-foreground/40 underline-offset-4 hover:bg-primary/10",
                            editable && "hover:bg-primary/10",
                            calcSel && calcSel.label === row.l && calcSel.anio === y && "ring-1 ring-primary bg-primary/10")}>
                          {enEdicion ? (
                            <input
                              autoFocus
                              value={editCel.txt}
                              onChange={(e) => setEditCel((s) => ({ ...s, txt: e.target.value }))}
                              onBlur={() => guardarDiaCascada(row.edit, y)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") e.currentTarget.blur();
                                if (e.key === "Escape") setEditCel(null);
                              }}
                              className="w-16 text-right font-mono text-xs border border-primary rounded px-1 py-0.5 bg-background"
                              data-testid={`nof-edit-${row.edit}-${y}`}
                            />
                          ) : (
                            <>
                              {row.f(row.g(y))}
                              {editable && <span className="ml-1 text-[10px] text-primary align-middle">✎</span>}
                            </>
                          )}
                        </TableCell>
                        );
                      })}
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
            {/* E2 (Héctor 29-09): la operación del último valor clicado, con
                los importes reales que la producen. */}
            {calcSel && (
              <div className="mx-4 my-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-[12px]"
                data-testid="nof-calculo-detalle">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="font-semibold">{calcSel.label} · {calcSel.anio}</span>
                    <span className="block font-mono tabular-nums mt-0.5">{calcSel.texto}</span>
                  </div>
                  <button type="button" className="text-muted-foreground hover:text-foreground text-xs shrink-0"
                    onClick={() => setCalcSel(null)} aria-label="Cerrar cálculo">✕</button>
                </div>
              </div>
            )}
            {/* 13/14 (Héctor oct): la cascada se AUTO-EXPLICA — qué hipótesis
                está aplicando el modelo a este bloque y de dónde sale, con
                las mismas frases de los supuestos del consolidado. */}
            {(() => {
              const prefijos = tabNof === "general"
                ? ["Clientes", "Existencias", "Proveedores", "NOF", "Ratio NOF"]
                : [LABELS[tabNof]?.split(" ")[0], "Ratio NOF de " + (LABELS[tabNof]?.split(" ")[0] || ""), "NOF con cuentas"];
              const relevantes = (resultado?.supuestos || []).filter((s) =>
                prefijos.some((p) => p && s.startsWith(p)));
              if (!relevantes.length) return null;
              return (
                <div className="mx-4 my-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-[11px]"
                  data-testid="nof-hipotesis-aplicada">
                  <span className="font-semibold">Hipótesis que está aplicando el modelo:</span>
                  <ul className="list-disc pl-4 mt-0.5 space-y-0.5">
                    {relevantes.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              );
            })()}
            <p className="px-4 py-2 text-[11px] text-muted-foreground">
              Cada bloque parte del <b>saldo bruto</b> del balance y resta las cuentas excluidas (fila ámbar) → <b>saldo ajustado</b>. El <b>día general</b> se calcula sobre el bruto; el <b>ajustado</b> sobre el operativo. La <b>NOF ajustada</b> (fila azul) es la real; la general incluye el intercompany e infla la cifra. Las celdas <u>subrayadas</u> muestran su cálculo al hacer clic. En los años proyectados, la fila del <b>ratio ajustado (✎)</b> es tu hipótesis: haz clic para editarla año a año — el modelo calcula el saldo ajustado (base × días ÷ 365), le suma el bloque excluido y lleva la variación a la caja.
            </p>
          </CardContent>
        </Card>
      )}

      {tabNof !== "general" && (
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle>Excluir cuentas del ciclo operativo (bruto vs ajustado)</CardTitle>
          <button type="button" onClick={() => setOrdenAsc((v) => !v)}
            className="text-[11px] text-muted-foreground hover:text-foreground border rounded px-2 py-1 shrink-0 tabular-nums"
            title="Ordenar las cuentas por importe">
            Importe {ordenAsc ? "↑ menor" : "↓ mayor"}
          </button>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Marca las cuentas que NO deben contar en el cálculo de PMC/PME/PMP (intercompany, dudoso cobro, stock deteriorado…). El ratio se mide sobre el saldo <b>operativo</b> (total − marcadas) y el importe marcado se mantiene en el balance.
          </p>

          {/* Selector del año MOSTRADO en las listas (Pau 2026-09-14): permite
              ver la evolución del saldo de cada cuenta año a año. Es solo
              visualización — la exclusión y su cálculo anclan SIEMPRE al
              último año real. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Saldo mostrado:</span>
            {aniosHist.map((y) => {
              const sel = (anioVista ?? ultimoReal) === y;
              return (
                <button key={y} type="button" onClick={() => setAnioVista(y)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-xs font-semibold tabular-nums transition-colors",
                    sel
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  )}
                  data-testid={`nof-anio-vista-${y}`}
                  title={y === ultimoReal ? "Último año real (año de anclaje del cálculo)" : `Ver los saldos de ${y}`}>
                  {y}{y === ultimoReal ? " ●" : ""}
                </button>
              );
            })}
            {(anioVista ?? ultimoReal) !== ultimoReal && (
              <span className="text-[11px] text-amber-600 dark:text-amber-400">
                Solo visualización: la exclusión ancla en {ultimoReal}
              </span>
            )}
          </div>

          {/* Mini-sumador en vivo: total excluido por bloque, resalta el tocado. */}
          <div className="flex flex-wrap gap-2">
            {clavesVista.map((clave) => {
              const s = sumaExcluida(clave);
              const activo = ultimoToque === clave;
              const hay = s.total > 0;
              return (
                <div key={clave}
                  className={cn("flex items-center gap-2 rounded-full border px-3 py-1 transition-all duration-200 motion-reduce:transition-none",
                    hay ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30"
                        : "border-border bg-muted/20 text-muted-foreground",
                    activo && "ring-2 ring-amber-400/70 scale-[1.04] shadow-sm")}>
                  <span className="text-xs font-medium">{LABELS[clave].split(" ")[0]}</span>
                  {s.n > 0 && <span className="text-[10px] rounded-full bg-amber-200/70 dark:bg-amber-900/60 px-1.5 py-0.5 font-semibold tabular-nums">{s.n}</span>}
                  <span className="text-xs font-mono tabular-nums font-semibold">{hay ? fmt(s.total) : "sin excluir"}</span>
                  {s.manual > 0 && <span className="text-[10px] text-muted-foreground" title="incluye importe manual">+man</span>}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 gap-3">
            {clavesVista.map((clave) => {
              const a = String(anioVista ?? ultimoReal ?? "");
              const cuentas = [...(cuentasNof?.partidas?.[clave] || [])].sort((x, y) => {
                const vx = Math.abs(x.por_anio?.[a] ?? 0);
                const vy = Math.abs(y.por_anio?.[a] ?? 0);
                return ordenAsc ? vx - vy : vy - vx;
              });
              const nMarc = marcadas[clave]?.size || 0;
              return (
                <div key={clave} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {LABELS[clave].split(" ")[0]}{nMarc > 0 ? ` · ${nMarc} excluida${nMarc > 1 ? "s" : ""}` : ""}
                  </Label>
                  <div className="border rounded-md max-h-72 overflow-y-auto divide-y">
                    {cuentas.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground px-2 py-2">Sin cuentas</p>
                    ) : (
                      cuentas.map((c) => (
                        <label key={c.codigo}
                          className={cn("flex items-center gap-2 px-2 py-1 text-[12px] cursor-pointer hover:bg-muted/30",
                            marcadas[clave]?.has(c.codigo) && "bg-amber-50 dark:bg-amber-950/30")}>
                          <input type="checkbox" className="h-3.5 w-3.5 accent-primary shrink-0"
                            checked={marcadas[clave]?.has(c.codigo) || false}
                            onChange={() => toggleCuenta(clave, c.codigo)} disabled={readOnly}
                            data-testid={`nof-cta-${c.codigo}`} />
                          <span className="font-mono shrink-0">
                            {codigoVisible(c.codigo)}
                            {esCuentaDeAsiento(c.codigo) && <SimboloAsiento className="ml-0.5" />}
                          </span>
                          <span className="flex-1 truncate" title={c.nombre}>{c.nombre}</span>
                          <span className="font-mono tabular-nums text-muted-foreground shrink-0">{fmt(c.por_anio?.[a])}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {nMarc === 0 && legado[clave] && (
                    <div className="rounded-md border border-amber-300 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-950/30 p-2 space-y-1.5"
                      data-testid={`nof-legado-${clave}`}>
                      <p className="text-[11px]">
                        <span className="font-medium">Importe excluido fijado en el antiguo panel «Parámetros»</span>{" "}
                        para {ultimoReal}. El cálculo lo resta como si fueran cuentas marcadas;
                        si marcas cuentas en este bloque, la lista sustituye a este importe.
                      </p>
                      {Object.keys(legado[clave]).some((y) => String(y) !== String(ultimoReal)) && (
                        <p className="text-[11px] text-amber-700 dark:text-amber-300">
                          Hay importes guardados para otros años (
                          {Object.entries(legado[clave])
                            .filter(([y]) => String(y) !== String(ultimoReal))
                            .map(([y, v]) => `${y}: ${fmt(v)}`)
                            .join(" · ")}
                          ). La proyección parte de {ultimoReal}: esos importes NO restan; escribe aquí el de {ultimoReal} si debe contar.
                        </p>
                      )}
                      <div className="flex items-center gap-2">
                        <Input type="number" step="1" min="0" className="h-8 w-40"
                          value={legadoTxt[clave] ?? ""} disabled={readOnly}
                          onChange={(e) => setLegadoTxt((v) => ({ ...v, [clave]: e.target.value }))}
                          onBlur={() => guardarLegado(clave, legadoTxt[clave])}
                          data-testid={`nof-legado-input-${clave}`} />
                        <span className="text-xs text-muted-foreground">€</span>
                        {!readOnly && (
                          <button type="button" className="text-xs text-primary hover:underline"
                            onClick={() => { setLegadoTxt((v) => ({ ...v, [clave]: "" })); guardarLegado(clave, "", true); }}
                            data-testid={`nof-legado-quitar-${clave}`}>
                            Quitar
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="pt-2 border-t">
            <Label className="text-xs font-medium">Otros a excluir (importe manual)</Label>
            <p className="text-[11px] text-muted-foreground mb-2">
              Importe libre a restar del saldo operativo de cada bloque (además de las cuentas marcadas). Se ancla a {ultimoReal ?? "el último real"} y se mantiene en la proyección. Ej.: en Proveedores restar 40.000 → baja el PMP y se ajusta toda la proyección.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {clavesVista.map((clave) => (
                <div key={clave} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{LABELS[clave].split(" ")[0]} — € manual</Label>
                  <Input type="number" step="1" min="0" className="h-8" placeholder="0 €"
                    value={manual[clave] ?? ""} disabled={readOnly}
                    onChange={(e) => { setManual((v) => ({ ...v, [clave]: e.target.value })); setUltimoToque(clave); }}
                    onBlur={handleManualBlur} data-testid={`nof-manual-${clave}`} />
                  {/* E7: importe distinto por año proyectado (vacío = arrastra). */}
                  {(det?.anios_proyectados || []).length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      <span className="w-full text-[10px] text-muted-foreground">
                        Por año (proyección) — vacío arrastra el de {ultimoReal}:
                      </span>
                      {det.anios_proyectados.map((y) => (
                        <div key={y} className="flex flex-col items-center">
                          <span className="text-[9px] text-muted-foreground tabular-nums">{y}</span>
                          <Input type="number" step="1" min="0"
                            className="h-7 w-[5.5rem] text-xs" placeholder="—"
                            value={manualAnual[`${clave}|${y}`] ?? ""} disabled={readOnly}
                            onChange={(e) => setManualAnual((v) => ({ ...v, [`${clave}|${y}`]: e.target.value }))}
                            onBlur={handleManualBlur}
                            data-testid={`nof-manual-${clave}-${y}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* E4 (Pau oct-2026): cuentas FUERA DE LA BASE de los plazos. En el
          bloque Clientes se marcan VENTAS (70x: intercompany, AIE…); en
          Existencias/Proveedores, COMPRAS (60x/61x). Sin esto, excluir un
          cliente del saldo deja sus ventas inflando la base y el PMC ajustado
          sale irreal (Edser: 9 días cuando el real sobre terceros es ~28). */}
      {tabNof !== "general" && (() => {
        const claveBase = tabNof === "clientes" ? "ventas" : "compras";
        const lista = cuentasNof?.bases?.[claveBase] || [];
        const a = String(anioVista ?? ultimoReal ?? "");
        const nMarc = marcadasBase[claveBase]?.size || 0;
        if (lista.length === 0) return null;
        return (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>
                {claveBase === "ventas"
                  ? "Excluir ventas de la base del PMC"
                  : "Excluir compras de la base del PME/PMP"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-[11px] text-muted-foreground">
                {claveBase === "ventas"
                  ? "Marca las cuentas de ventas (70x) que no generan saldo de clientes operativo (intercompany, AIE…): el PMC se mide sobre las ventas a terceros, en histórico y en proyección."
                  : "Marca las cuentas de compras (60x/61x) de proveedores excluidos: el PME y el PMP se miden sobre la base real a terceros."}
                {nMarc > 0 && <b> · {nMarc} marcada{nMarc > 1 ? "s" : ""}</b>}
              </p>
              <div className="border rounded-md max-h-72 overflow-y-auto divide-y">
                {lista.map((c) => (
                  <label key={c.codigo}
                    className={cn("flex items-center gap-2 px-2 py-1 text-[12px] cursor-pointer hover:bg-muted/30",
                      marcadasBase[claveBase]?.has(c.codigo) && "bg-amber-50 dark:bg-amber-950/30")}>
                    <input type="checkbox" className="h-3.5 w-3.5 accent-primary shrink-0"
                      checked={marcadasBase[claveBase]?.has(c.codigo) || false}
                      onChange={() => toggleCuentaBase(claveBase, c.codigo)} disabled={readOnly}
                      data-testid={`nof-base-${c.codigo}`} />
                    <span className="font-mono shrink-0">
                      {codigoVisible(c.codigo)}
                      {esCuentaDeAsiento(c.codigo) && <SimboloAsiento className="ml-0.5" />}
                    </span>
                    <span className="flex-1 truncate" title={c.nombre}>{c.nombre}</span>
                    <span className="font-mono tabular-nums text-muted-foreground shrink-0">{fmt(c.por_anio?.[a])}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {MOSTRAR_PALANCAS && tabNof !== "general" && det && (det.anios_proyectados || []).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Palancas del bloque excluido (proyección)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-[11px] text-muted-foreground">
              Hacen que el bloque excluido (p.ej. el intercompany) <b>declive año a año</b> en vez de quedarse plano.
              <b className="text-emerald-700 dark:text-emerald-400"> Cobro</b>: reduce el bloque y <b>libera caja</b>.
              <b className="text-amber-700 dark:text-amber-400"> Baja incobrable</b>: reduce el bloque como <b>pérdida no monetaria</b> — resta del resultado y del patrimonio neto, pero <b>no toca la tesorería</b>.
            </p>
            {clavesVista.filter((c) => sumaExcluida(c).total > 0).length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                Marca cuentas (o pon un importe manual) arriba para poder aplicarles palancas.
              </p>
            ) : (
              clavesVista.filter((c) => sumaExcluida(c).total > 0).map((clave) => (
                <div key={clave} className="space-y-1">
                  <Label className="text-xs font-medium">
                    {LABELS[clave].split(" ")[0]} · bloque al {ultimoReal}: <span className="font-mono tabular-nums">{fmt(sumaExcluida(clave).total)}</span>
                  </Label>
                  <div className="overflow-x-auto border rounded-md">
                    <Table className={cn(TABLA_ANIOS, "text-xs")}>
                      <ColsAnios n={det.anios_proyectados.length} />
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead>Palanca</TableHead>
                          {det.anios_proyectados.map((y) => (
                            <TableHead key={y} className="text-right">{y}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[
                          { t: "cobro", l: "Cobro (entra caja)", cls: "text-emerald-700 dark:text-emerald-400" },
                          { t: "baja", l: "Baja incobrable (no monetaria)", cls: "text-amber-700 dark:text-amber-400" },
                        ].map(({ t, l, cls }) => (
                          <TableRow key={t}>
                            <TableCell className={cn("font-medium", cls)}>{l}</TableCell>
                            {det.anios_proyectados.map((y) => (
                              <TableCell key={y} className="p-1">
                                <Input type="number" step="1" min="0" className="h-7 text-right" placeholder="0"
                                  value={palancas[`${clave}|${t}|${y}`] ?? ""} disabled={readOnly}
                                  onChange={(e) => setPalancas((v) => ({ ...v, [`${clave}|${t}|${y}`]: e.target.value }))}
                                  onBlur={handlePalancaBlur} data-testid={`nof-pal-${clave}-${t}-${y}`} />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                        <TableRow className="border-t-2 font-semibold bg-primary/5">
                          <TableCell>= Bloque resultante</TableCell>
                          {det.anios_proyectados.map((y) => (
                            <TableCell key={y} className="text-right font-mono tabular-nums">
                              {fmt(cel(clave, y, "excluir_cuentas"))}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

    </div>
  );
}

// ── Evolución de la NOF ajustada por año (rediseño 2026-09-29) ──────────────
// Vista "de financiero": BARRAS con el nivel de NOF ajustada (gris histórico ·
// amarillo proyectado), el % s/ventas bajo cada año (la eficiencia real: la NOF
// absoluta crece con las ventas) y la VARIACIÓN traducida a CAJA entre barras
// (rojo = consume caja, verde = libera). SVG propio, solo lectura de
// nof_detalle: no altera ningún cálculo.
function NofEvolucionChart({ det, aniosDet, esProy }) {
  const datos = aniosDet.map((y) => {
    const d = det?.nof?.por_anio?.[String(y)] || {};
    return {
      anio: y,
      v: d.ajustada,
      pct: d.ajustada_ventas,
      var: d.var_ajustada,
      proy: esProy(y),
    };
  });
  const conValor = datos.filter((d) => d.v != null && !isNaN(d.v));
  if (conValor.length < 2) return null;

  const n = datos.length;
  const W = 760;
  const H = 210;
  const PAD_X = 24;
  const PAD_TOP = 30;   // etiqueta de importe sobre la barra
  const PAD_BOT = 52;   // año + % s/ventas + Δ caja
  const min = Math.min(0, ...conValor.map((d) => d.v));
  const max = Math.max(0, ...conValor.map((d) => d.v));
  const rango = max - min || 1;
  const areaH = H - PAD_TOP - PAD_BOT;
  const y = (v) => PAD_TOP + ((max - v) * areaH) / rango;
  const slotW = (W - 2 * PAD_X) / n;
  const barW = Math.min(56, slotW * 0.55);
  const cx = (i) => PAD_X + i * slotW + slotW / 2;

  const fmtC = (v) =>
    Math.abs(v) >= 1e6
      ? `${(v / 1e6).toFixed(1).replace(".", ",")}M`
      : `${Math.round(v / 1e3)}k`;
  const fmtPct = (p) => (p == null || isNaN(p) ? null : `${(p * 100).toFixed(1).replace(".", ",")}%`);

  return (
    <Card data-testid="nof-evolucion-chart">
      <CardHeader className="pb-2">
        <CardTitle>Evolución de la NOF ajustada</CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Barras: NOF ajustada (la real) por año — gris histórico · amarillo proyectado — con su
          <b> % sobre ventas</b> debajo. La cifra en <span className="text-rose-600 font-semibold">rojo consume caja</span> y
          la <span className="text-emerald-600 font-semibold">verde la libera</span>: es la Var. NOF, el efecto directo en tesorería.
        </p>
      </CardHeader>
      <CardContent>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img"
          aria-label="NOF ajustada por año: nivel, % sobre ventas y efecto en caja">
          {/* Eje cero */}
          <line x1={PAD_X - 6} x2={W - PAD_X + 6} y1={y(0)} y2={y(0)}
            stroke="currentColor" strokeOpacity="0.3" />
          {datos.map((d, i) => {
            const sinValor = d.v == null || isNaN(d.v);
            const x0 = cx(i) - barW / 2;
            const yTop = sinValor ? null : Math.min(y(d.v), y(0));
            const hBar = sinValor ? 0 : Math.max(2, Math.abs(y(d.v) - y(0)));
            const pct = fmtPct(d.pct);
            const varOk = d.var != null && !isNaN(d.var) && Math.round(d.var) !== 0;
            const libera = varOk && d.var < 0;
            return (
              <g key={d.anio}>
                {!sinValor && (
                  <>
                    <rect x={x0} y={yTop} width={barW} height={hBar} rx="3"
                      className={d.proy ? "fill-primary" : "fill-muted-foreground"}
                      fillOpacity={d.proy ? 0.85 : 0.45} />
                    <text x={cx(i)} y={yTop - 7} textAnchor="middle"
                      className={cn("text-[11px] font-mono tabular-nums font-semibold",
                        d.proy ? "fill-primary" : "fill-muted-foreground")}>
                      {fmtC(d.v)}
                    </text>
                  </>
                )}
                {/* Año */}
                <text x={cx(i)} y={H - PAD_BOT + 16} textAnchor="middle"
                  className={cn("text-[11px]",
                    d.proy ? "fill-primary font-semibold" : "fill-muted-foreground")}>
                  {d.anio}{d.proy ? " ·P" : ""}
                </text>
                {/* % s/ventas: la eficiencia, comparable entre años */}
                {pct && (
                  <text x={cx(i)} y={H - PAD_BOT + 29} textAnchor="middle"
                    className="text-[10px] fill-muted-foreground">
                    {pct} s/ventas
                  </text>
                )}
                {/* Δ caja vs año anterior: el dato que mira un financiero */}
                {varOk && (
                  <text x={cx(i)} y={H - PAD_BOT + 43} textAnchor="middle"
                    className={cn("text-[10px] font-mono tabular-nums font-semibold",
                      libera ? "fill-emerald-600" : "fill-rose-600")}>
                    {libera ? "▼ libera " : "▲ consume "}{fmtC(Math.abs(d.var))}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </CardContent>
    </Card>
  );
}
